import { Facebook, Share2, Twitter, Calendar, Clock, MapPin, Trophy, Mic2 } from "lucide-react";
import { SEO } from "@/lib/seo";
import { PageHero } from "@/components/common/PageHero";
import { Reveal } from "@/components/common/Reveal";
import { LiveStreamPlayer } from "@/components/stream/LiveStreamPlayer";
import { STREAM_STATUS } from "@/config/site";
import { toast } from "sonner";

export default function Live() {
  const { isLive, match, upcoming } = STREAM_STATUS;

  const handleShare = (platform) => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    if (platform === "copy") {
      navigator.clipboard?.writeText(url);
      toast("Link copied to clipboard");
      return;
    }
    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent("Watch live grassroots women's football coverage")}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    };
    window.open(shareUrls[platform], "_blank", "noopener,noreferrer");
  };

  return (
    <div data-testid="live-page">
      <SEO
        title="Live Women's Football Coverage"
        description="Watch live women's grassroots football coverage and commentary from WomensMatchDayLive across Kick, Twitch, YouTube and TikTok."
        path="/live"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SportsEvent",
          name: "WomensMatchDayLive coverage",
          eventStatus: isLive ? "https://schema.org/EventScheduled" : "https://schema.org/EventScheduled",
        }}
      />
      <PageHero eyebrow="Live" title="Live" subtitle="Watch grassroots women's football commentary as it happens." crumbLabel="Live" />

      <section className="bg-brand-black py-16">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 lg:grid-cols-12 lg:px-12">
          <div className="lg:col-span-8">
            <Reveal>
              <LiveStreamPlayer isLive={isLive} />
            </Reveal>

            <Reveal delay={0.1} className="mt-6 flex flex-wrap items-center gap-3 border-t border-border-dark pt-6">
              <span className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-white/40">
                <Share2 className="h-3.5 w-3.5" /> Share:
              </span>
              <button type="button" data-testid="share-twitter" onClick={() => handleShare("twitter")} className="border border-border-dark p-2 text-white/60 hover:border-brand-purple hover:text-white transition-colors">
                <Twitter className="h-4 w-4" />
              </button>
              <button type="button" data-testid="share-facebook" onClick={() => handleShare("facebook")} className="border border-border-dark p-2 text-white/60 hover:border-brand-purple hover:text-white transition-colors">
                <Facebook className="h-4 w-4" />
              </button>
              <button type="button" data-testid="share-copy-link" onClick={() => handleShare("copy")} className="border border-border-dark px-3 py-2 font-subheading text-xs uppercase tracking-wide text-white/60 hover:border-brand-purple hover:text-white transition-colors">
                Copy Link
              </button>
            </Reveal>
          </div>

          <div className="lg:col-span-4">
            <Reveal delay={0.15} className="border border-border-dark bg-surface-dark p-6">
              <h2 className="font-heading text-2xl uppercase text-white">Match Information</h2>
              <dl className="mt-5 space-y-4 text-sm">
                <div className="flex items-center justify-between border-b border-border-dark pb-3">
                  <dt className="flex items-center gap-2 text-white/40"><Trophy className="h-4 w-4" /> Competition</dt>
                  <dd className="text-white">{match.competition}</dd>
                </div>
                <div className="flex items-center justify-between border-b border-border-dark pb-3">
                  <dt className="text-white/40">Home</dt>
                  <dd className="text-white">{match.homeTeam}</dd>
                </div>
                <div className="flex items-center justify-between border-b border-border-dark pb-3">
                  <dt className="text-white/40">Away</dt>
                  <dd className="text-white">{match.awayTeam}</dd>
                </div>
                <div className="flex items-center justify-between border-b border-border-dark pb-3">
                  <dt className="flex items-center gap-2 text-white/40"><Clock className="h-4 w-4" /> Kick-off</dt>
                  <dd className="text-white">{match.kickOff}</dd>
                </div>
                <div className="flex items-center justify-between border-b border-border-dark pb-3">
                  <dt className="flex items-center gap-2 text-white/40"><MapPin className="h-4 w-4" /> Venue</dt>
                  <dd className="text-white">{match.venue}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="flex items-center gap-2 text-white/40"><Mic2 className="h-4 w-4" /> Commentary</dt>
                  <dd className="text-white">Text goes here</dd>
                </div>
              </dl>
            </Reveal>

            <Reveal delay={0.2} className="mt-6 border border-border-dark p-6">
              <h3 className="flex items-center gap-2 font-heading text-xl uppercase text-white">
                <Calendar className="h-4 w-4 text-brand-purple" /> Upcoming Coverage
              </h3>
              <p className="mt-3 text-sm text-white/60">{upcoming}</p>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
